// ./src/utils/api/EventGuestInviteeService.ts
//
// ROADMAP A2b #62 P6(c) Phase B — the OTHER ADULTS of an invited family. A family is ONE guest (the lead, carrying the
// group size); every other adult the host ticks gets their own link to that same invitation. They are created by the
// import (`EventGuestService.importGuests` with `alsoNotifyPersonIds`, or a family member of someone already invited),
// so this service only READS them and REMOVES one. Mirrors eventsmagic-api `eventGuestInviteeTypes.ts`.
//
// ⚠ A separate query on purpose — not a field on the guest list: an un-recomposed gateway rejects a whole query that
//   names an unknown field, and the guest list must never go dark because of this one.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { listAllPages } from "./listAllPages";

const FIELDS = `
  id
  eventId
  eventGuestId
  personId
  person { id firstName lastName email phone }
  lastSentAt
  lastVisitedAt
  rights
`;

const LIST_QUERY = gql`
  query EventGuestInviteeList($filter: EventGuestInviteeFilter, $params: PaginationAndSorting) {
    eventGuestInviteeList(filter: $filter, params: $params) {
      code
      data { ${FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const REMOVE_MUTATION = gql`
  mutation EventGuestInviteeRemove($where: EventGuestInviteeWhereInput) {
    eventGuestInviteeRemove(where: $where) {
      code
      data { id }
      errors { name errors warnings }
    }
  }
`;

export interface EventGuestInvitee {
  id?: string;
  eventId?: string | null;
  /** The family's invitation — its lead guest. */
  eventGuestId?: string | null;
  personId?: string | null;
  person?: { id?: string; firstName?: string | null; lastName?: string | null; email?: string | null; phone?: string | null } | null;
  lastSentAt?: string | null;
  lastVisitedAt?: string | null;
  rights?: string[] | null;
}

export class EventGuestInviteeService {
  /**
   * The adults linked to these invitations (a page of guests), read WHOLE. ⚠ ROADMAP #118: it asked for the id count ×
   * "a family's likely size" (6), so one invitation with eight adults on a page of one came back with six.
   */
  static async listForGuests(eventId: string, eventGuestIds: string[]): Promise<OpResult> {
    return listAllPages<EventGuestInvitee>(
      (filter, pagination) => ApiService.executeQuery(LIST_QUERY, { filter, params: { pagination } }),
      { eventId: [eventId], eventGuestId: eventGuestIds },
    );
  }

  /** Stop sending this adult the family's invitation (their link stops working; the invitation is untouched). */
  static async remove(id: string): Promise<OpResult> {
    return ApiService.executeMutation(REMOVE_MUTATION, { where: { id } });
  }
}
